/** FLD-OFF-FIX-3 — the link detector. Offline is a statement about the NETWORK, never about how
 *  busy this app is.
 *
 *  What it was: every axios rejection called `noteResult(false)` — an HTTP 404, a 422, a 413 over
 *  the size limit, a cancelled request and a timeout caused by our own blocked main thread all
 *  counted the same. Two of them inside a minute read as "no signal", and the request interceptor
 *  then answered every call locally, so the app sat offline on a wired desktop. Proven on preview:
 *  four 404s, or three answers slower than the 4.5s client timeout, and nothing left the browser.
 *
 *  The rule now:
 *  - A reply of ANY status means the network works — 4xx/5xx are app news, not link news.
 *  - Our own cancellation, a body refused for size, and a validation error never count.
 *  - DRW-LOAD-C: a TIMEOUT never counts at all, blocked thread or not. It is always "inconclusive"
 *    and always asks the worker probe instead, because a slow server, a stalled storage read and a
 *    dev-server recompile all look identical from here and none of them is a dead network.
 *  - Only a named network error (refused / DNS / reset / ERR_NETWORK) or a FAILED WORKER PROBE
 *    counts as a real failure.
 *  - Offline needs N consecutive real failures spread over a minimum window (per device profile).
 *  - One real success leaves offline immediately.
 *  - While a server-dependent operation is in flight (an upload, an ingest poll) only a network
 *    failure OF THAT OPERATION can take the app offline.
 *  - The connectivity check runs in a Web Worker, so heavy main-thread work cannot delay it.
 */

/** §5 — one detector, two profiles, constants in code (never a Settings toggle). */
export const PROFILES = {
  desktop: { fails: 3, windowMs: 15000, label: "desktop" },
  field: { fails: 3, windowMs: 8000, label: "field" },
};
const fieldDevice = () => {
  if (typeof window === "undefined") return false;
  const coarse = window.matchMedia?.("(pointer: coarse)")?.matches || false;
  const mobileUa = /Android|iPhone|iPad|iPod|Mobile|Silk|Kindle/i.test(navigator.userAgent || "");
  return coarse || mobileUa;
};
export const PROFILE = fieldDevice() ? PROFILES.field : PROFILES.desktop;

export const SLOW_MS = 2500;        // "weak signal" wording only — never a reason to go offline
const PROBE_TIMEOUT_MS = 6000;      // the worker's own budget; nothing here blocks a tap
const BLOCK_TICK_MS = 250;          // main-thread watchdog
const BLOCK_LATE_MS = 750;          // a tick this late means the thread was blocked
const BLOCK_GRACE_MS = 1500;        // a timeout this soon after a block is inconclusive

const state = {
  fails: [],                 // timestamps of consecutive REAL network failures
  offline: false,
  lastOkAt: 0,
  lastCheckedAt: 0,
  lastReason: "",
  recent: [],                // { ok, ms, at } — for the weak/good wording
  critical: [],              // server-dependent operations in flight, each with a deadline
  blocks: [],                // { from, to } main-thread stalls
  trail: [],                 // the last real failures, for the report and the QA hook
};

const listeners = new Set();
export const onLink = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = () => listeners.forEach((fn) => { try { fn(); } catch { /* ui */ } });

/** §1 — heavy work must not be able to speak for the network. The watchdog records when this
 *  thread stalled so a timeout inside that stall is thrown away rather than believed. */
if (typeof window !== "undefined") {
  let last = Date.now();
  setInterval(() => {
    const now = Date.now();
    if (now - last > BLOCK_TICK_MS + BLOCK_LATE_MS) {
      state.blocks.push({ from: last, to: now });
      if (state.blocks.length > 20) state.blocks.shift();
    }
    last = now;
  }, BLOCK_TICK_MS);
}
export const wasBlocked = (from, to) =>
  state.blocks.some((b) => b.to + BLOCK_GRACE_MS >= from && b.from <= to);
export const blockReport = () => [...state.blocks];

/** A server-dependent operation: while one is open, nothing ELSE may declare the link dead.
 *  Every window is BOUNDED — a warm-up that runs for minutes must not switch the detector off
 *  for ever, so a token that is never closed expires on its own and the detector resumes. */
export const CRITICAL_TTL_MS = 45000;
export function beginCritical(ttlMs = CRITICAL_TTL_MS) {
  const token = { expires: Date.now() + ttlMs, ttl: ttlMs };
  state.critical.push(token);
  const release = () => { state.critical = state.critical.filter((t) => t !== token); };
  // a long operation (an 84MB ingest) renews its window on every sign of life, so the gate lasts
  // exactly as long as the work and not a second longer
  release.renew = () => { token.expires = Date.now() + token.ttl; };
  return release;
}
export function endCritical() { state.critical.pop(); }
export const criticalInFlight = () => {
  const now = Date.now();
  state.critical = state.critical.filter((t) => t.expires > now);
  return state.critical.length;
};

const TIMEOUT = (e) => e?.code === "ECONNABORTED" || e?.code === "ETIMEDOUT" || /timeout/i.test(e?.message || "");
const CANCELLED = (e) => e?.code === "ERR_CANCELED" || e?.name === "CanceledError" || e?.name === "AbortError";
const NET_CODES = new Set(["ERR_NETWORK", "ECONNREFUSED", "ENOTFOUND", "EAI_AGAIN", "ERR_NAME_NOT_RESOLVED",
  "ERR_INTERNET_DISCONNECTED", "ERR_CONNECTION_REFUSED", "ERR_CONNECTION_RESET", "ERR_ADDRESS_UNREACHABLE"]);

/** reached | ignored | netfail | inconclusive — the whole classification lives here. */
export function classify(err) {
  const cfg = err?.config || {};
  if (err?.response) return "reached";                       // 4xx / 5xx: the server answered
  if (err?._localFirst) return "ignored";                    // our own local-first short circuit
  if (err?.device_full) return "ignored";                    // no room on the device, not the link
  if (CANCELLED(err)) return "ignored";                      // we aborted it ourselves
  if (TIMEOUT(err)) {
    // DRW-LOAD-C §1 — a timeout is NEVER evidence on its own. It can mean a slow server, a stalled
    // object-storage read, a blocked main thread or a dev-server recompile; only the worker probe
    // can tell whether the NETWORK is down, and `noteFail` asks it on every failure. One 28s
    // storage stall used to count as a lost connection here, three of them put a wired desktop
    // offline.
    return "inconclusive";
  }
  if (NET_CODES.has(err?.code) || err?.message === "Network Error") return "netfail";
  return "inconclusive";                                     // anything we cannot name is not evidence
}

export function noteOk(ms = 0, { heavy = false } = {}) {
  state.lastOkAt = Date.now();
  state.lastCheckedAt = state.lastOkAt;
  state.fails = [];
  // MARK-LINK-1 §13 — a 5MB chunk PUT or an ingest poll legitimately takes seconds; letting its
  // duration into the latency average is what made the chip read "Weak signal" through an upload on
  // a perfectly good connection. It still proves the link is UP, it just says nothing about speed.
  if (!heavy) state.recent.push({ ok: true, ms: Number(ms) || 0, at: state.lastOkAt });
  trim();
  if (state.offline) {
    state.offline = false;
    state.lastReason = "";
    // DRW-LOAD-C §7 — the chip must not keep saying "weak" for a minute after the link is proven
    // back; the outage's failures are history, not the current state.
    state.recent = state.recent.filter((r) => r.ok);
  }
  emit();
}

/** A genuine network failure. `critical` = this request IS the server-dependent operation.
 *  `timeout` = there was no answer at all rather than a refused connection. */
export function noteFail({ critical = false, reason = "", timeout = false } = {}) {
  const now = Date.now();
  state.lastCheckedAt = now;
  state.recent.push({ ok: false, ms: 0, at: now, reason });
  state.trail.push({ at: now, reason, critical, timeout });
  if (state.trail.length > 15) state.trail.shift();
  trim();
  if (criticalInFlight() > 0 && !critical) { emit(); maybeCheck(); return false; }   // §1 last rule
  // While OUR OWN operation is running the server can be flat out — an 84MB ingest blocks it for
  // minutes — and a request with no answer says nothing about the network. During an operation
  // only a genuinely dead connection (refused / DNS / reset) counts; silence does not.
  if (criticalInFlight() > 0 && timeout) { emit(); return false; }
  state.fails.push(now);
  if (state.fails.length > 50) state.fails = state.fails.slice(-50);
  // the window is measured from the FIRST failure of the current unbroken run — a success
  // anywhere in between clears the run, so this really is "N failures over ≥ W seconds".
  const spread = now - state.fails[0];
  if (!state.offline && state.fails.length >= PROFILE.fails && spread >= PROFILE.windowMs) {
    state.offline = true;
    state.lastReason = reason || `${state.fails.length} failed requests over ${Math.round(spread / 1000)}s`;
    console.info(`[link] offline — ${state.lastReason} (${PROFILE.label} profile: ${PROFILE.fails} fails over ${PROFILE.windowMs / 1000}s)`);
  }
  emit();
  if (!state.offline) maybeCheck();     // ask the ground truth rather than guess from one failure
  return state.offline;
}

/** A failure anywhere asks the worker for the truth, at most once every few seconds. The worker
 *  answer is what the detector believes — a request that failed for its own reasons never gets to
 *  decide, and a real outage is confirmed in seconds instead of waiting for the next 20s timer. */
let checking = false;
let lastCheckStarted = 0;
const CHECK_THROTTLE_MS = 3000;
function maybeCheck() {
  if (checking || Date.now() - lastCheckStarted < CHECK_THROTTLE_MS) return;
  lastCheckStarted = Date.now();
  checking = true;
  checkLink().finally(() => { checking = false; });
}

/** DRW-LOAD-C §1 — something failed for a reason that says nothing about the network (a timeout, a
 *  stalled storage read, a recompile). Ask the ground truth instead of counting it. */
export function noteInconclusive() { maybeCheck(); }

/** DRW-LOAD-C §7 — the way back without a reload. While offline the worker probe runs every few
 *  seconds; while online it only runs when nothing has been heard for a while. One tiny request,
 *  off the main thread, so neither state can get stuck on a stale verdict. */
const OFFLINE_RETRY_MS = 5000;
const IDLE_CHECK_MS = 30000;
if (typeof window !== "undefined") {
  setInterval(() => {
    if (document.visibilityState === "hidden") return;
    const quiet = Date.now() - Math.max(state.lastOkAt, state.lastCheckedAt);
    if (state.offline ? quiet >= OFFLINE_RETRY_MS : quiet >= IDLE_CHECK_MS) maybeCheck();
  }, 2500);
}

function trim() {
  const cut = Date.now() - 60000;
  state.recent = state.recent.filter((r) => r.at >= cut).slice(-12);
}

export const isLinkDown = () => state.offline;
export const lastCheckedAt = () => state.lastCheckedAt;
export const lastReason = () => state.lastReason;
export const consecutiveFails = () => state.fails.length;

/** good | weak | offline — wording for the chip. Weak never changes behaviour on its own. */
export function linkQuality() {
  if (state.offline) return "offline";
  const oks = state.recent.filter((r) => r.ok);
  // DRW-LOAD-C §7 — a failure is only news about the CURRENT state for a few seconds. Counting a
  // minute of history made the chip read "weak signal" long after the link was proven back.
  const fails = state.recent.filter((r) => !r.ok && r.at >= Date.now() - 15000).length;
  if (!state.recent.length) return "good";
  const avg = oks.length ? oks.reduce((s, r) => s + r.ms, 0) / oks.length : 0;
  return fails > 0 || avg > SLOW_MS ? "weak" : "good";
}

// ── the check: a tiny uncached request, run off the main thread ──────────────────────────────
const WORKER_SRC = `
self.onmessage = async (e) => {
  const { url, token, timeoutMs, id } = e.data || {};
  const t0 = Date.now();
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeoutMs);
  try {
    const r = await fetch(url + (url.includes("?") ? "&" : "?") + "_=" + t0, {
      method: "GET", cache: "no-store", signal: ac.signal,
      headers: token ? { Authorization: "Bearer " + token } : {},
    });
    // ANY status means the link is alive — 401 included.
    self.postMessage({ id, reached: true, status: r.status, ms: Date.now() - t0 });
  } catch (err) {
    self.postMessage({ id, reached: false, aborted: ac.signal.aborted, ms: Date.now() - t0, message: String(err && err.message || err) });
  } finally { clearTimeout(timer); }
};
`;

let worker = null;
let workerBroken = false;
const waiting = new Map();
function getWorker() {
  if (worker || workerBroken) return worker;
  try {
    const url = URL.createObjectURL(new Blob([WORKER_SRC], { type: "text/javascript" }));
    worker = new Worker(url);
    worker.onmessage = (e) => { const w = waiting.get(e.data?.id); if (w) { waiting.delete(e.data.id); w(e.data); } };
    worker.onerror = () => { workerBroken = true; worker = null; };
  } catch { workerBroken = true; }
  return worker;
}
export const probeRunsInWorker = () => !!getWorker();

const PING_URL = () => `${window.location.origin}/api/field/offline/ping`;
/** The only way back from offline that does not depend on the main thread being free. */
export async function checkLink() {
  const url = PING_URL();
  const token = (() => { try { return localStorage.getItem("auth_token") || ""; } catch { return ""; } })();
  const w = getWorker();
  let out;
  if (w) {
    const id = Math.random().toString(36).slice(2);
    out = await new Promise((resolve) => {
      const done = (d) => resolve(d);
      waiting.set(id, done);
      setTimeout(() => { if (waiting.delete(id)) resolve({ reached: false, aborted: true, ms: PROBE_TIMEOUT_MS + 500 }); }, PROBE_TIMEOUT_MS + 500);
      w.postMessage({ url, token, timeoutMs: PROBE_TIMEOUT_MS, id });
    });
  } else {
    // no worker in this browser: still a bare fetch, never the busy axios pipeline
    const t0 = Date.now();
    try {
      const r = await fetch(`${url}?_=${t0}`, { cache: "no-store", headers: token ? { Authorization: `Bearer ${token}` } : {} });
      out = { reached: true, status: r.status, ms: Date.now() - t0 };
    } catch (e) { out = { reached: false, ms: Date.now() - t0, message: String(e?.message || e) }; }
  }
  if (out.reached) { noteOk(out.ms); return true; }
  noteFail({ critical: true, timeout: !!out.aborted,
    reason: out.aborted ? "the connectivity check timed out" : "the connectivity check could not reach the server" });
  state.lastCheckedAt = Date.now();
  emit();
  return false;
}

/** QA harness (same idea as `window.__absFieldApi`): drive and read the detector from a console.
 *  Read-only state plus the two actions a tester needs — nothing here is used by the app. */
if (typeof window !== "undefined") {
  window.__absLink = {
    state: () => ({ offline: state.offline, fails: state.fails.length, lastOkAt: state.lastOkAt,
      lastCheckedAt: state.lastCheckedAt, reason: state.lastReason, critical: criticalInFlight(),
      profile: PROFILE, quality: linkQuality(), inWorker: probeRunsInWorker(), blocks: state.blocks.length,
      trail: state.trail.slice(-8) }),
    check: () => checkLink(),
    classify,
    beginCritical,
    endCritical,
  };
}
